import {
  ChatAssistantWalkthroughScenarioUpdateDocument,
  ChatAssistantWalkthroughScenarioUpdateMutation,
  ChatAssistantWalkthroughScenarioUpdateMutationVariables,
  ChatAssistantWalkthroughFeatureListDocument,
  ChatAssistantWalkthroughFeatureListQuery,
  ChatAssistantWalkthroughFeatureListQueryVariables,
  ChatAssistantWalkthroughFeatureAcknowledgeDocument,
  ChatAssistantWalkthroughFeatureAcknowledgeMutation,
  ChatAssistantWalkthroughFeatureAcknowledgeMutationVariables,
  ChatAssistantAskDocument,
  ChatAssistantAskQuery,
  ChatAssistantAskQueryVariables,
  ChatAssistantGuideListDocument,
  ChatAssistantGuideListQuery,
  ChatAssistantGuideListQueryVariables,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IAssistantAnswerReturnType,
  IAssistantGuidesReturnType,
  IAssistantWalkthroughFeaturesReturnType,
  IAssistantWalkthroughFeatureAcknowledgeReturnType,
  IAssistantWalkthroughScenarioUpdateReturnType,
} from "./types";

export async function askAssistant(
  variables: ChatAssistantAskQueryVariables
): Promise<IAssistantAnswerReturnType | undefined> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest<
      ChatAssistantAskQuery,
      ChatAssistantAskQueryVariables
    >({
      query: ChatAssistantAskDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    return res.data?.chatAssistantAsk;
  } catch (error) {
    console.error(
      "Error fetching chat answer:",
      error instanceof Error ? error.name : "Unknown error"
    );
    throw error;
  }
}

export async function getAssistantGuides(
  variables: ChatAssistantGuideListQueryVariables
): Promise<IAssistantGuidesReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest<
      ChatAssistantGuideListQuery,
      ChatAssistantGuideListQueryVariables
    >({
      query: ChatAssistantGuideListDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    return res.data?.chatAssistantGuideList.guides ?? [];
  } catch (error) {
    console.error(
      "Error fetching chat guides:",
      error instanceof Error ? error.name : "Unknown error"
    );
    throw error;
  }
}

export async function getAssistantWalkthroughFeatures(
  variables: ChatAssistantWalkthroughFeatureListQueryVariables = {}
): Promise<IAssistantWalkthroughFeaturesReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest<
      ChatAssistantWalkthroughFeatureListQuery,
      ChatAssistantWalkthroughFeatureListQueryVariables
    >({
      query: ChatAssistantWalkthroughFeatureListDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    return (
      res.data?.chatAssistantWalkthroughFeatureList.walkthroughFeatures ?? []
    );
  } catch (error) {
    console.error(
      "Error fetching assistant feature offers:",
      error instanceof Error ? error.name : "Unknown error"
    );
    throw error;
  }
}

export async function acknowledgeAssistantWalkthroughFeature(
  variables: ChatAssistantWalkthroughFeatureAcknowledgeMutationVariables
): Promise<IAssistantWalkthroughFeatureAcknowledgeReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest<
      ChatAssistantWalkthroughFeatureAcknowledgeMutation,
      ChatAssistantWalkthroughFeatureAcknowledgeMutationVariables
    >({
      mutation: ChatAssistantWalkthroughFeatureAcknowledgeDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (res.data?.chatAssistantWalkthroughFeatureAcknowledge === true) {
      executeEviction(client, {
        typename: "WalkthroughFeatureChatModel",
        id: variables.walkthroughFeatureId,
        fieldNames: ["chatAssistantWalkthroughFeatureList"],
      });
    }

    return res.data?.chatAssistantWalkthroughFeatureAcknowledge;
  } catch (error) {
    console.error(
      "Error saving assistant feature offer state:",
      error instanceof Error ? error.name : "Unknown error"
    );
    throw error;
  }
}

export async function updateAssistantWalkthroughScenario(
  variables: ChatAssistantWalkthroughScenarioUpdateMutationVariables
): Promise<IAssistantWalkthroughScenarioUpdateReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest<
      ChatAssistantWalkthroughScenarioUpdateMutation,
      ChatAssistantWalkthroughScenarioUpdateMutationVariables
    >({
      mutation: ChatAssistantWalkthroughScenarioUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (res.data?.chatAssistantWalkthroughScenarioUpdate === true) {
      executeEviction(client, {
        typename: "WalkthroughFeatureChatModel",
        id: variables.walkthroughFeatureId,
        fieldNames: ["chatAssistantWalkthroughFeatureList"],
      });
    }

    return res.data?.chatAssistantWalkthroughScenarioUpdate;
  } catch (error) {
    console.error(
      "Error saving assistant scenario progress:",
      error instanceof Error ? error.name : "Unknown error"
    );
    throw error;
  }
}
